// src/pages/Activities.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Calendar, MapPin, Tag } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { fetchActivities } from "../features/activity/activitySlice";
import { useNavigate } from "react-router-dom"; // ✅ gunakan untuk navigasi

const Activities = () => {
  const { t } = useLanguage();
  const dispatch = useDispatch();
  const navigate = useNavigate(); // ✅ hook router
  const { activitiesList, loading, pagination } = useSelector(
    (state) => state.activity
  );

  const [page, setPage] = useState(1);
  const size = 6;

  useEffect(() => {
    dispatch(fetchActivities({ page, size }));
  }, [dispatch, page, size]);

  const categoryColors = {
    Important: "rgba(255, 87, 34, 0.1)",
    Info: "rgba(33, 150, 243, 0.1)",
    Success: "rgba(13, 121, 22, 0.1)",
  };

  const handleNext = () => {
    if (pagination?.current_page < pagination?.last_page) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (pagination?.current_page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const handleCardClick = (slug) => {
    navigate(`/activities/${slug}`); // ✅ navigasi ke halaman detail
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        style={{
          padding: "8rem 1.5rem 4rem",
          background:
            "radial-gradient(at 50% 30%, hsla(108,100%,50%,0.2) 0px, transparent 50%)",
          textAlign: "center",
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1
            style={{
              fontWeight: 700,
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
              lineHeight: 0.9,
              color: "rgb(0, 55, 32)",
              marginBottom: "1.5rem",
            }}
          >
            {t.activities.title}
          </h1>
          <p
            style={{
              fontSize: "clamp(1.125rem, 2.5vw, 1.25rem)",
              lineHeight: 1.5,
              color: "rgb(131, 146, 140)",
              maxWidth: "700px",
              margin: "0 auto",
            }}
          >
            {t.activities.subtitle}
          </p>
        </div>
      </section>

      {/* Activities Grid */}
      <section style={{ padding: "4rem 1.5rem 6rem", background: "white" }}>
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Loading...</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {activitiesList?.map((event) => (
                  <Card
                    key={event.id}
                    className="activity-card cursor-pointer hover:shadow-lg"
                    style={{
                      background: "white",
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      borderRadius: "12px",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    onClick={() => handleCardClick(event.slug)} // ✅ klik card
                  >
                    {/* Image */}
                    <div
                      style={{
                        width: "100%",
                        height: "220px",
                        backgroundColor: "#f9f9f9",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={event.image}
                        alt={event.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.3s ease",
                        }}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/400x200?text=No+Image";
                        }}
                      />
                    </div>

                    {/* Content */}
                    <div style={{ padding: "1.5rem", flex: 1 }}>
                      <Badge
                        style={{
                          background:
                            categoryColors[event.label] ||
                            "rgba(0, 0, 0, 0.05)",
                          color: "rgb(0, 55, 32)",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "9999px",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          border: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "0.25rem",
                        }}
                      >
                        <Tag size={12} /> {event.label}
                      </Badge>

                      <h3
                        style={{
                          fontWeight: 600,
                          fontSize: "1.125rem",
                          color: "rgb(0, 55, 32)",
                          margin: "1rem 0 0.5rem",
                        }}
                      >
                        {event.title}
                      </h3>

                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: "rgb(100, 100, 100)",
                          marginBottom: "0.75rem",
                        }}
                      >
                        <Calendar size={14} className="inline-block mr-1" />
                        {event.start_date} - {event.end_date}
                        <br />
                        <MapPin size={14} className="inline-block mr-1" />{" "}
                        {event.location}
                      </div>

                      <p
                        style={{
                          fontSize: "0.9rem",
                          color: "rgb(60, 60, 60)",
                          marginBottom: "1.25rem",
                          lineHeight: 1.5,
                        }}
                      >
                        {event.description}
                      </p>

                      <span
                        style={{
                          color: "rgb(13, 121, 22)",
                          fontWeight: 600,
                        }}
                      >
                        {t.nav.home === "Home"
                          ? "Read More"
                          : "Baca Selengkapnya"}{" "}
                        →
                      </span>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {pagination && (
                <div className="flex justify-center items-center gap-4 mt-10">
                  <button
                    onClick={handlePrev}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-full border border-green-600 text-green-600 disabled:opacity-50"
                  >
                    ← {t.nav.home === "Home" ? "Previous" : "Sebelumnya"}
                  </button>
                  <span className="text-sm text-gray-600">
                    {t.nav.home === "Home" ? "Page" : "Halaman"} {page} /{" "}
                    {pagination?.last_page}
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={page === pagination?.last_page}
                    className="px-4 py-2 rounded-full border border-green-600 text-green-600 disabled:opacity-50"
                  >
                    {t.nav.home === "Home" ? "Next" : "Berikutnya"} →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <style jsx>{`
        .activity-card:hover img {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default Activities;
